/* @jsx h */
import {
  defineComponent,
  h,
  ref,
  computed,
  Teleport,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { ExportRun } from "../exports";

type Presentation = { id: string; kind: "pdf" | "xlsx" };

const MENU_WIDTH = 224;

const KIND_ICON: Record<string, string> = {
  pdf: "picture_as_pdf",
  xlsx: "table_view",
};

export const DownloadMenu = defineComponent({
  name: "DataRoomDownloadMenu",
  props: {
    presentations: { type: Array, default: () => [] },
    run: { type: Object, default: null },
    onStart: { type: Function, required: true },
    onReset: { type: Function, required: true },
    onDownload: { type: Function, required: true },
  },
  setup(props: any) {
    const { t } = useLocalization();

    const open = ref(false);
    const triggerRef = ref<HTMLElement | null>(null);
    const menuPos = ref<{ top: number; left: number }>({ top: 0, left: 0 });

    const run = computed<ExportRun | null>(() => props.run ?? null);
    const status = computed(() => run.value?.status ?? "idle");
    const busy = computed(
      () => status.value === "starting" || status.value === "running",
    );
    const ready = computed(() => status.value === "done");
    const errored = computed(() => status.value === "error");

    const kindLabel = (kind: "pdf" | "xlsx") =>
      kind === "pdf"
        ? t("reports.dataroom.format_pdf")
        : t("reports.dataroom.format_excel");

    const otherPresentations = computed<Presentation[]>(() => {
      const current = run.value?.presentation;
      return (props.presentations as Presentation[]).filter(
        (p) => p.kind !== current,
      );
    });

    // --- positioning / open-close ---
    const reposition = () => {
      const el = triggerRef.value;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      menuPos.value = {
        top: rect.bottom + 6,
        left: Math.max(8, rect.right - MENU_WIDTH),
      };
    };
    const onDocClick = (ev: MouseEvent) => {
      if (!open.value) return;
      if (triggerRef.value?.contains(ev.target as Node)) return;
      if ((ev.target as HTMLElement)?.closest?.("[data-dataroom-menu]")) return;
      close();
    };
    const addListeners = () => {
      document.addEventListener("click", onDocClick, true);
      window.addEventListener("resize", reposition);
      window.addEventListener("scroll", reposition, true);
    };
    const removeListeners = () => {
      document.removeEventListener("click", onDocClick, true);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
    const close = () => {
      open.value = false;
      removeListeners();
    };
    const openMenu = async () => {
      if (open.value) return close();
      open.value = true;
      await nextTick();
      reposition();
      addListeners();
    };
    onBeforeUnmount(removeListeners);

    // --- actions ---
    const start = (kind: "pdf" | "xlsx") => {
      close();
      props.onStart(kind);
    };
    const doDownload = () => {
      if (run.value) props.onDownload(run.value.id);
    };
    const reset = () => {
      close();
      props.onReset();
    };

    // Primary click: download when ready, otherwise show the dropdown.
    const onPrimaryClick = () => {
      if (ready.value) doDownload();
      else openMenu();
    };

    const stageLabel = (r: ExportRun): string => {
      const s = r.stage;
      if (s === "rendering" || s === "verifying" || s === "finalizing-data")
        return t("reports.dataroom.export_rendering");
      if (s === "extracting-rows" || s === "extracting-summary")
        return `${t("reports.dataroom.export_extracting")}${r.rows ? ` · ${r.rows}` : ""}`;
      return t("reports.dataroom.export_queued");
    };

    // --- button appearance ---
    const buttonContent = () => {
      if (busy.value && run.value) {
        return (
          <>
            <span class="loading loading-spinner loading-xs" />
            <span>{stageLabel(run.value)}</span>
          </>
        );
      }
      if (ready.value && run.value) {
        return (
          <>
            <span class="material-symbols-outlined text-base">
              {KIND_ICON[run.value.presentation]}
            </span>
            <span>
              {t("reports.dataroom.download")} {kindLabel(run.value.presentation)}
            </span>
          </>
        );
      }
      if (errored.value) {
        return (
          <>
            <span class="material-symbols-outlined text-base">error</span>
            <span>{t("reports.dataroom.export_failed")}</span>
          </>
        );
      }
      return (
        <>
          <span class="material-symbols-outlined text-base">download</span>
          <span>{t("reports.dataroom.download")}</span>
        </>
      );
    };

    const buttonClass = computed(() => {
      if (ready.value) return "btn-success";
      if (errored.value) return "btn-error btn-outline";
      return "btn-primary";
    });

    // --- dropdown content ---
    const menuItem = (opts: {
      icon: string;
      label: string;
      onClick: () => void;
      danger?: boolean;
    }) => (
      <li>
        <a class={opts.danger ? "text-error" : ""} onClick={opts.onClick}>
          <span class="material-symbols-outlined text-base">{opts.icon}</span>
          {opts.label}
        </a>
      </li>
    );

    const menuContent = () => {
      const presentations = props.presentations as Presentation[];

      // Idle: pick a format to start.
      if (status.value === "idle") {
        if (!presentations.length) {
          return (
            <div class="px-3 py-2 text-xs text-base-content/70">
              {t("reports.dataroom.export_none")}
            </div>
          );
        }
        return (
          <ul class="menu p-0">
            <li class="menu-title px-3 py-1 text-[11px] uppercase tracking-wider text-accent/60">
              {t("reports.dataroom.export_as")}
            </li>
            {presentations.map((p) =>
              menuItem({
                icon: KIND_ICON[p.kind],
                label: kindLabel(p.kind),
                onClick: () => start(p.kind),
              }),
            )}
          </ul>
        );
      }

      // Busy: show state + start over.
      if (busy.value && run.value) {
        return (
          <div class="p-1">
            <div class="flex items-center gap-2 px-3 py-2 text-sm text-base-content/70">
              <span class="loading loading-spinner loading-xs" />
              {stageLabel(run.value)}
            </div>
            <ul class="menu p-0">
              {menuItem({
                icon: "restart_alt",
                label: t("reports.dataroom.export_start_over"),
                onClick: reset,
                danger: true,
              })}
            </ul>
          </div>
        );
      }

      // Ready / error: download again + export other format + reset.
      return (
        <ul class="menu p-0">
          {ready.value &&
            run.value &&
            menuItem({
              icon: "download",
              label: `${t("reports.dataroom.download_again")} (${kindLabel(run.value.presentation)})`,
              onClick: () => {
                close();
                doDownload();
              },
            })}
          {errored.value &&
            run.value &&
            menuItem({
              icon: "refresh",
              label: t("reports.dataroom.export_retry"),
              onClick: () => start(run.value!.presentation),
            })}
          {otherPresentations.value.length > 0 && (
            <li class="menu-title px-3 py-1 text-[11px] uppercase tracking-wider text-accent/60">
              {t("reports.dataroom.export_other")}
            </li>
          )}
          {otherPresentations.value.map((p) =>
            menuItem({
              icon: KIND_ICON[p.kind],
              label: `${t("reports.dataroom.export_as")} ${kindLabel(p.kind)}`,
              onClick: () => start(p.kind),
            }),
          )}
          <div class="divider my-0.5" />
          {menuItem({
            icon: "restart_alt",
            label: t("reports.dataroom.export_reset"),
            onClick: reset,
            danger: true,
          })}
        </ul>
      );
    };

    return () => (
      <div class="flex items-center">
        <div class="join">
          <button
            ref={triggerRef}
            class={`btn btn-sm join-item gap-1.5 ${buttonClass.value}`}
            onClick={onPrimaryClick}
          >
            {buttonContent()}
          </button>
          {/* Caret always opens the dropdown (state / reset / other format). */}
          <button
            class={`btn btn-sm btn-square join-item ${buttonClass.value}`}
            onClick={openMenu}
          >
            <span class="material-symbols-outlined text-base">
              {open.value ? "arrow_drop_up" : "arrow_drop_down"}
            </span>
          </button>
        </div>

        {open.value && (
          <Teleport to="body">
            <div
              data-dataroom-menu
              class="fixed z-[60] rounded-2xl bg-base-100 shadow-xl p-1.5"
              style={{
                top: `${menuPos.value.top}px`,
                left: `${menuPos.value.left}px`,
                width: `${MENU_WIDTH}px`,
              }}
            >
              {menuContent()}
            </div>
          </Teleport>
        )}
      </div>
    );
  },
});
